import { useId, useRef, useState } from 'react';
import { Button } from '@aigovops/design-system';
import { reconnectGitHubConnection } from '../lib/lifecycle-api';
import { ApiError } from '../lib/api';
import { useOptionalWorkspace } from '../lib/workspace';
import { AskAdmin } from './AskAdmin';

/** The one action a GitHub return message offers: start Connect again, or open the GAC Pro app on GitHub. */
export interface GitHubReturnAction { kind: 'connect' | 'install'; label: string }
export interface GitHubReturn { ok: boolean; text: string; action?: GitHubReturnAction }

const LOGIN = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,38})$/;

/**
 * Back from GitHub (?github=reconnected | failed, &reason=…, &owner=…): what to tell the person and the one action
 * that fixes it (V108-8-1: one message per reason, never a dead end), or null for anything else.
 */
export function githubReturnMessage(github: string | null, reason: string | null, rawOwner?: string | null, rawMerged?: string | null): GitHubReturn | null {
  const owner = rawOwner && LOGIN.test(rawOwner) ? rawOwner : null;
  if (github === 'reconnected') {
    // V112-3-5 (BL-31): a reconnect that folded duplicates says so (`merged` = how many connections are now one).
    const merged = rawMerged && /^\d{1,2}$/.test(rawMerged) ? Number(rawMerged) : 0;
    return { ok: true, text: merged >= 2 && owner ? `GitHub reconnected. Your ${merged} GitHub connections for ${owner} are now one.` : 'GitHub reconnected.' };
  }
  if (github !== 'failed') return null;
  const on = owner ?? 'that account';
  const retry: GitHubReturnAction = { kind: 'connect', label: 'Try again' };
  switch (reason) {
    case 'denied': return { ok: false, text: 'GitHub sign-in was cancelled.', action: { kind: 'connect', label: 'Connect GitHub' } };
    case 'expired': case 'used': return { ok: false, text: 'GitHub sign-in took too long. Try again.', action: retry };
    case 'no_installation': return { ok: false, text: owner ? `Install the GAC Pro app on ${owner} first.` : 'Install the GAC Pro app on your GitHub account or organization first.',
      action: { kind: 'install', label: owner ? `Install the GAC Pro app on ${owner}` : 'Install the GAC Pro app' } };
    case 'no_repositories': return { ok: false, text: `The GAC Pro app on ${on} can't read any repositories yet. Add one on GitHub, then connect.`, action: { kind: 'install', label: 'Add repositories on GitHub' } };
    case 'suspended': return { ok: false, text: `The GAC Pro app is suspended on ${on}. Unsuspend it on GitHub, then connect.`, action: { kind: 'install', label: 'Open the app on GitHub' } };
    case 'permissions': return { ok: false, text: `The GAC Pro app on ${on} asks for more than read access, so GAC Pro didn't connect. Review its permissions on GitHub, then connect.`, action: { kind: 'install', label: 'Review the app on GitHub' } };
    case 'repository': return { ok: false, text: `The GAC Pro app can't read that repository on ${on}. Connect again and pick from the repositories it can read.`, action: { kind: 'connect', label: 'Connect GitHub' } };
    case 'reauth': return { ok: false, text: 'Your GitHub sign-in expired. Connect GitHub again to add repositories.', action: { kind: 'connect', label: 'Connect GitHub' } };
    default: return { ok: false, text: "GitHub didn't finish connecting. Try again.", action: retry };
  }
}

/** The words for a failed start; never the API's error codes. */
function startError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 403) return 'Only a workspace owner or admin can reconnect GitHub.';
    if (e.status === 404) return 'This GitHub connection was removed. Connect GitHub again from Setup.';
    if (e.status === 503) return 'GitHub is not available in this workspace.';
  }
  return 'GitHub could not be opened. Try again.';
}

/**
 * 2026-10-12: sign in to GitHub again for an existing connection (no new connection). GitHub's consent page opens
 * in this tab and returns to this page (?github=reconnected | failed). People who can't manage connections get the
 * ask-an-admin link instead. `target` (the repository) goes into the accessible name so several buttons differ.
 * `announce={false}` when the host is already an alert, so a start failure is not read twice.
 */
export function ReconnectGitHub({ slug, connectorId, target, compact, variant = 'primary', announce = true }: {
  slug: string; connectorId: string; target?: string | null; compact?: boolean; variant?: 'primary' | 'secondary' | 'ghost'; announce?: boolean;
}) {
  const ws = useOptionalWorkspace();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const busyRef = useRef(false);
  const errId = useId();
  if (ws && ws.role !== 'owner' && ws.role !== 'admin') {
    return <AskAdmin slug={slug} compact verb="reconnect GitHub" what="manages connections" subject="GAC Pro: reconnect GitHub" />;
  }
  const start = async () => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setErr(null);
    try {
      const { url } = await reconnectGitHubConnection(slug, connectorId, `${window.location.pathname}${window.location.search}`);
      window.location.assign(url);
    } catch (e) {
      setErr(startError(e));
      busyRef.current = false; setBusy(false);
    }
  };
  return (
    <span className="gac-reconnect">
      <Button compact={compact} variant={variant} aria-disabled={busy || undefined} aria-busy={busy || undefined}
        aria-label={target ? `Reconnect GitHub for ${target}` : undefined} aria-describedby={err ? errId : undefined}
        onClick={() => void start()}>{busy ? 'Opening GitHub…' : 'Reconnect GitHub'}</Button>
      {err ? <span id={errId} role={announce ? 'alert' : undefined} className="gac-reconnect-err">{err}</span> : null}
    </span>
  );
}
